import { Typography, withStyles, withTheme } from '@material-ui/core';

import { FilteringState, IntegratedFiltering } from '@devexpress/dx-react-grid';
import { Grid, Table, TableFilterRow, TableHeaderRow } from '@devexpress/dx-react-grid-material-ui';
import { IntegratedSorting, SortingState } from '@devexpress/dx-react-grid';
import ReactRouterPropTypes from 'react-router-prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { compose } from 'recompose';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import classNames from 'classnames';

import { formatDateGeneric } from 'utilities/common';
import { formatMoney } from 'utilities/currencyFormatter';
import Log from 'utilities/log';
import Spinner from 'modules/common/spinner.component';
import { ZERO_GUID } from 'modules/authorizations/details/destinationServices/destinationServicesDetailsEditableCard.component';

const styles = theme => {
  const intakeRecordBorder = `1px solid ${theme.palette.grey[500]}`;
  return {
    table: { borderSpacing: theme.spacing(0, 2) },
    tableHead: { height: 32 },
    cell: {
      borderTop: intakeRecordBorder,
      borderBottom: intakeRecordBorder,
      borderLeft: 0,
      borderRight: 0,
      '&:first-child': {
        borderTopLeftRadius: theme.shape.borderRadius,
        borderBottomLeftRadius: theme.shape.borderRadius,
        borderLeft: intakeRecordBorder
      },
      '&:last-child': {
        borderTopRightRadius: theme.shape.borderRadius,
        borderBottomRightRadius: theme.shape.borderRadius,
        borderRight: intakeRecordBorder
      }
    },
    urgencyMenuItem: { justifyContent: 'center' },
    selectInput: {
      lineHeight: 1.75,
      paddingBottom: theme.spacing(0.5),
      paddingRight: theme.spacing(4),
      paddingTop: theme.spacing(0.5)
    },
    row: { backgroundColor: theme.palette.common.white },
    noDataCell: {
      paddingBottom: theme.spacing(12),
      paddingTop: theme.spacing(12),
      textAlign: 'center'
    }
  };
};

const FILTER_DISABLED_COLUMNS = ['organization'];

export const STATUS_NOT_VIEWED_ID = '6f3b939e-1f43-4a8b-a103-dbb3f1d3f4f9';
export const STATUS_ON_HOLD_ID = '4ae21fb8-0b9b-4d9a-9b41-0f96e2680d9a';
export const STATUS_VIEWED_ID = '4b9b6e2a-3ab0-44a9-8e54-8ea0db4a3518';

class ExpensesGrid extends PureComponent {
  static defaultProps = {
    isLoading: true
  };

  constructor(props) {
    super(props);

    this.state = {
      columns: [
        { name: 'statusId', title: 'Status', getCellValue: this.getSetUrgencyCellValue },
        { name: 'authorizationId', title: 'Auth-ID' },
        { name: 'authName', title: 'Auth Name' },
        { name: 'expenseId', title: 'Expense Id' },
        { name: 'dateSubmitted', title: 'Date Submitted', getCellValue: this.formatDate('dateSubmitted') },
        { name: 'description', title: 'Description' },
        { name: 'total', title: 'Total' },
        { name: 'expenseAnalyst', title: 'Expense Analyst' },
        { name: 'numberOfLineItems', title: 'Num Lines' }
      ],
      filters: [],
      sorting: [{ columnName: 'dateSubmitted', direction: 'desc' }],
      integratedFilteringColumnExtensions: [],
      tableColumnExtensions: []
    };

    this.changeSorting = sorting => this.setState({ sorting });
  }

  keyPredicate = (value, filter) => {
    const valueLower = (value?.key || '').toLowerCase();
    const filterLower = (filter?.value || '').toLowerCase();
    return valueLower.indexOf(filterLower) > -1;
  };

  formatDate = key => record => formatDateGeneric(record[key]);

  getSetUrgencyCellValue = record => {
    const {
      theme: { palette }
    } = this.props;
    const date = record.dateSubmitted ? new Date(record.dateSubmitted) : null;
    const today = new Date();
    const diffTime = date ? Math.abs(today - date) : null;
    const diffDays = diffTime ? Math.ceil(diffTime / (1000 * 60 * 60 * 24)) : null;

    return (
      <div>
        {record.statusId === STATUS_NOT_VIEWED_ID && diffDays >= 3 && (
          <FontAwesomeIcon
            color={palette.primary.main}
            icon={['fas', 'exclamation-circle']}
            size="lg"
            title="Not viewed for the past 3 business days or more."
          />
        )}
        {(record.statusId === STATUS_NOT_VIEWED_ID || record.statusId === ZERO_GUID) && (
          <FontAwesomeIcon color={palette.warning.main} icon={['fas', 'circle']} size="lg" title="Not viewed." />
        )}
        {record.statusId === STATUS_ON_HOLD_ID && (
          <FontAwesomeIcon color={palette.error.main} icon={['fas', 'exclamation-triangle']} size="lg" title="On Hold." />
        )}
        {record.statusId === STATUS_VIEWED_ID && (
          <FontAwesomeIcon color={palette.primary.main} icon={['fas', 'check-circle']} size="lg" title="Viewed." />
        )}
      </div>
    );
  };

  changeFilters = filters => this.setState({ filters });

  filterCell = props =>
    props.column.title && !FILTER_DISABLED_COLUMNS.includes(props.column.name) ? <TableFilterRow.Cell {...props} /> : null;

  headerCell = props => (props.column.title ? <TableHeaderRow.Cell {...props} /> : null);

  headerRowComponent = props => <TableHeaderRow.Row className={this.props.classes.tableHead} {...props} />;

  rowComponent = props => <Table.Row className={this.props.classes.row} {...props} />;

  cellComponent = props => {
    const clickHandler = () => this.props.onSelect(props.row.expenseId);
    const pointerClass = 'handles-click';
    const className = classNames(this.props.classes.cell, pointerClass);
    if (props.column.name === 'total') {
      return (
        <Table.Cell onClick={clickHandler} className={className} {...props}>
          {formatMoney(props.value)}
        </Table.Cell>
      );
    }
    return <Table.Cell onClick={clickHandler} className={className} {...props} />;
  };

  tableComponent = props => <Table.Table className={this.props.classes.table} {...props} />;

  noDataCellComponent = props => {
    const content = this.props.isLoading ? <Spinner logo /> : 'No Records were returned.  Check the filters above.';
    const { getMessage, ...rest } = props;
    return (
      <Table.Cell {...rest} className={this.props.classes.noDataCell}>
        <Typography>{content}</Typography>
      </Table.Cell>
    );
  };

  render() {
    Log.trace('RENDER', 'expensesGrid');
    const { expenses } = this.props;
    const { columns, filters, tableColumnExtensions, integratedFilteringColumnExtensions, sorting } = this.state;

    return (
      <Grid rows={expenses} columns={columns}>
        <SortingState sorting={sorting} onSortingChange={this.changeSorting} />
        <FilteringState filters={filters} onFiltersChange={this.changeFilters} />
        <IntegratedFiltering columnExtensions={integratedFilteringColumnExtensions} />
        <IntegratedSorting />
        <Table
          columnExtensions={tableColumnExtensions}
          rowComponent={this.rowComponent}
          tableComponent={this.tableComponent}
          cellComponent={this.cellComponent}
          noDataCellComponent={this.noDataCellComponent}
        />
        <TableHeaderRow rowComponent={this.headerRowComponent} cellComponent={this.headerCell} showSortingControls />
        <TableFilterRow cellComponent={this.filterCell} />
      </Grid>
    );
  }
}

ExpensesGrid.propTypes = {
  expenses: PropTypes.array.isRequired,
  classes: PropTypes.object.isRequired,
  onSelect: PropTypes.func.isRequired,
  theme: PropTypes.object.isRequired,
  isLoading: PropTypes.bool,
  history: ReactRouterPropTypes.history.isRequired
};

export default compose(withStyles(styles), withTheme)(ExpensesGrid);
